/**
 * The History page's result card — README §7: channel tile, name + ticket,
 * a CLOSED/DISMISSED/LIVE chip, "channel · client · owner · closed date",
 * and the matching snippet. Fuller than the typeahead's single-line row on
 * purpose (task-19 resolution #4) — this is a `Card`, not a button-in-a-list.
 *
 * `threadState` is a small state machine the page above computes, not this
 * component: 'none' (no `threadId` on the search hit — a locator with
 * nothing to open, e.g. a company/person match with no associated thread;
 * rendered plainly, no link, since inventing a destination would violate
 * task-19 rule 2), 'loading' (a `threadId` exists, the enrichment fetch for
 * it hasn't resolved yet), or 'ready' (the real `ThreadDetail` to render
 * from). Every card's snippet is `item.snippet` — the server's own match
 * text — never the thread's close note or first message, even though both
 * are sitting right there once `thread` resolves (task-19 rule 1).
 *
 * The link always points at `/transcript/:id` regardless of live/closed —
 * `TranscriptPage` (Task 20) is the single place that decides whether that
 * lands on the read-only transcript or redirects to the live pane, by
 * reading the thread's current state itself (see SearchTypeahead's doc
 * comment for the same reasoning). A closed comm therefore never offers
 * any action that could write to it: this card has no button that isn't a
 * plain navigation link.
 */
import { Link } from 'react-router-dom'
import type { ThreadDetail } from '@communicator/shared'
import { Card, Chip, Icon } from '../ui'
import { CHANNEL_ICON, channelTileStyle } from '../rail/format'
import { formatClock } from '../thread/format'
import type { SearchGroupType, SearchResultItem } from '../../lib/ops'
import { COMM_STATUS_TONE, commStatus, highlightSnippet, metaLine, SEARCH_GROUP_LABEL } from './format'
import { selectTimeZone, useStore } from '../../store'

export type ResultCardThreadState = { status: 'ready'; thread: ThreadDetail } | { status: 'loading' } | { status: 'none' }

export interface ResultCardProps {
  item: SearchResultItem
  groupType: SearchGroupType
  threadState: ResultCardThreadState
  /** The query this result was found by — README §9's `<mark>`ed match. Optional so an existing caller that hasn't threaded it through yet degrades to no highlight, not a crash. */
  query?: string
}

/** README §9: '"the snippet in quotes with the match `<mark>`ed in --gold-100/--gold-700."' `--tint-accent`/`--on-tint-accent` are the same gold-100/gold-700 slots `RailRow.tsx`/`CallFloor.tsx`/`ThreadHeader.tsx` already use for exactly this pair. */
function Snippet({ text, query, className }: { text: string; query: string | undefined; className: string }) {
  const parts = highlightSnippet(text, query ?? '')
  return (
    <span className={className}>
      &ldquo;
      {parts.map((p, i) =>
        p.match ? (
          <mark key={i} style={{ background: 'var(--tint-accent)', color: 'var(--on-tint-accent)' }}>
            {p.text}
          </mark>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
      &rdquo;
    </span>
  )
}

export function ResultCard({ item, groupType, threadState, query }: ResultCardProps) {
  const timeZone = useStore((s) => selectTimeZone(s.cfg))
  if (threadState.status === 'none') {
    return (
      <Card data-testid={`result-locator-${item.id}`} className="flex flex-col gap-1 p-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-lo">{SEARCH_GROUP_LABEL[groupType]}</p>
        <p className="text-[13px] text-hi">{item.label}</p>
        <Snippet text={item.snippet} query={query} className="text-[12px] text-mid" />
      </Card>
    )
  }

  if (threadState.status === 'loading') {
    return (
      <Card data-testid={`result-loading-${item.id}`} className="flex flex-col gap-1 p-3">
        <p className="text-[13px] text-hi">{item.label}</p>
        <p className="text-[12px] text-lo">Loading details…</p>
      </Card>
    )
  }

  const { thread } = threadState
  const status = commStatus(thread.state, thread.reason)
  // README §9: "status pill or a mono duration with a timer icon for
  // calls." Only when there is a real finished-call duration to show — a
  // live call, or a voice thread the CDR has not yet reported a duration
  // for, falls back to the honest status pill instead of a blank/invented
  // chip.
  const callDurationS = thread.channel === 'voice' ? (thread.call?.durationS ?? null) : null

  return (
    <Card data-testid={`result-card-${item.id}`} className="overflow-hidden p-0">
      <Link to={`/transcript/${thread.id}`} data-testid={`result-card-link-${item.id}`} className="flex gap-2.5 p-3 no-underline">
        <span
          aria-hidden="true"
          className="flex h-[28px] w-[28px] flex-none items-center justify-center rounded-[8px]"
          style={channelTileStyle(thread.channel)}
        >
          <Icon name={CHANNEL_ICON[thread.channel]} size={15} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="truncate text-[13px] font-medium text-hi">{thread.who}</span>
            {thread.ticket ? (
              // Fix round 2 (finding 6): --on-tint-accent is legible on
              // --tint-accent, not on --surface-card (this card's own
              // background, no tint chip around this ticket number) —
              // --brand-accent-text is tuned for exactly this.
              <span className="font-mono text-[12px]" style={{ color: 'var(--brand-accent-text)' }}>
                {thread.ticket}
              </span>
            ) : null}
            {callDurationS !== null ? (
              <span className="inline-flex items-center gap-1 font-mono text-[12px] text-mid">
                <Icon name="timer" size={12} />
                {formatClock(callDurationS)}
              </span>
            ) : (
              <Chip tone={COMM_STATUS_TONE[status]}>{status}</Chip>
            )}
          </span>
          <span className="block text-[12px] text-mid">{metaLine(thread, timeZone)}</span>
          <Snippet text={item.snippet} query={query} className="mt-1 block text-[12px] text-lo" />
        </span>
      </Link>
    </Card>
  )
}
